import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/tabon/AppLayout";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Copy, Download, Plus, Trash2, Pencil, X, Star, Check,
  ArrowUp, ArrowDown, QrCode, Camera, Loader2, ImageIcon,
  Search, ChevronDown, ChevronRight, GripVertical, ArrowUpToLine, ListOrdered, Hash, Percent, MapPin,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  arrayMove, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Resolve /static URLs to include backend base URL
function resolveImgUrl(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/static")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base ? `${base}${url}` : url;
  }
  return url;
}

const ITEM_TYPES = [
  { v: "veg", label: "Veg", dot: "#22c55e" },
  { v: "nonveg", label: "Non-Veg", dot: "#ef4444" },
  { v: "vegan", label: "Vegan", dot: "#16a34a" },
  { v: "na", label: "Not Applicable (drinks, etc.)", dot: "transparent" },
];

export default function MenuPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [addonGroups, setAddonGroups] = useState([]); // native + POS-imported groups (with nested items + used_by_count)
  // Main-category management state
  const [newMainCatName, setNewMainCatName] = useState("");
  const [editingMC, setEditingMC] = useState(null);      // mc id being edited
  const [editMCName, setEditMCName] = useState("");
  const [mcImgUploading, setMCImgUploading] = useState(new Set());
  // Sub-category management state
  const [newCatName, setNewCatName] = useState("");
  const [newCatMainCategoryId, setNewCatMainCategoryId] = useState("");
  const [editingCat, setEditingCat] = useState(null);    // category id being edited
  const [editCatForm, setEditCatForm] = useState({ name: "", description: "", main_category_id: "" });
  const [catImgUploading, setCatImgUploading] = useState(new Set());
  const [itemModal, setItemModal] = useState(null); // { mode: 'create'|'edit', categoryId, item? }
  const [deliveryQr, setDeliveryQr] = useState(null); // { url, active, bonus }
  const [deliveryBonusInput, setDeliveryBonusInput] = useState("");
  const [roomServiceQr, setRoomServiceQr] = useState(null); // { url, active }
  const [collectionQr, setCollectionQr] = useState(null); // { url, active } — takeaway pickup link (AC-3)
  const [dineInQr, setDineInQr] = useState(null); // { url, active } — dine-in table link (AD-3)
  // Menu admin — search / filter / collapse / highlight (Phase 1)
  const [itemSearch, setItemSearch] = useState("");
  const [menuFilters, setMenuFilters] = useState([]); // "sold_out" | "no_photo" | "no_desc"
  const [collapsedCats, setCollapsedCats] = useState(new Set());
  const [highlightItemId, setHighlightItemId] = useState(null);
  const [stickyTop, setStickyTop] = useState(56);
  const [reorderingCat, setReorderingCat] = useState(null); // category id in reorder mode, or null
  const collapseInitRef = useRef(false);

  const load = async () => {
    const [s, mc, c, i, ag, dqr, rsqr, cqr, diqr] = await Promise.all([
      api.get("/menu/settings"),
      api.get("/menu/main-categories"),
      api.get("/menu/categories"),
      api.get("/menu/items"),
      api.get("/menu/addon-groups").catch(() => ({ data: [] })),
      api.get("/menu/delivery-qr").catch(() => ({ data: null })),
      api.get("/menu/room-service-qr").catch(() => ({ data: null })),
      api.get("/menu/collection-qr").catch(() => ({ data: null })),
      api.get("/menu/dine-in-qr").catch(() => ({ data: null })),
    ]);
    setSettings(s.data);
    setMainCategories(mc.data);
    setCategories(c.data);
    setItems(i.data);
    setAddonGroups(ag.data || []);
    if (dqr.data) {
      setDeliveryQr(dqr.data);
      setDeliveryBonusInput(String(dqr.data.bonus || 0));
    }
    if (rsqr.data) setRoomServiceQr(rsqr.data);
    if (cqr.data) setCollectionQr(cqr.data);
    if (diqr.data) setDineInQr(diqr.data);
  };

  useEffect(() => { load(); }, []);

  // Refresh just the addon groups (after an inline create/attach or a management
  // edit) without reloading the whole page — keeps counts and options current.
  const reloadAddonGroups = async () => {
    try {
      const { data } = await api.get("/menu/addon-groups");
      setAddonGroups(data || []);
    } catch { /* keep the list we already have */ }
  };

  const updateSettings = async (patch) => {
    try {
      const { data } = await api.put("/menu/settings", patch);
      setSettings(data);
      toast.success("Menu settings saved");
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(settings.public_url);
    toast.success("Public menu link copied");
  };

  const copyDeliveryLink = () => {
    if (deliveryQr?.url) {
      navigator.clipboard.writeText(deliveryQr.url);
      toast.success("Registration link copied");
    }
  };

  const downloadDeliveryQr = async () => {
    if (!deliveryQr?.url) return;
    try {
      const resp = await api.get("/menu/delivery-qr.png", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabon-delivery-registration.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyRoomServiceLink = () => {
    if (roomServiceQr?.url) {
      navigator.clipboard.writeText(roomServiceQr.url);
      toast.success("Room service link copied");
    }
  };

  const downloadRoomServiceQr = async () => {
    if (!roomServiceQr?.url) return;
    try {
      const resp = await api.get("/menu/room-service-qr.png", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabon-room-service-qr.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  // User-facing name is "Takeaway" (AE-2); the API path stays /menu/collection-qr and the state var
  // stays collectionQr — renaming an endpoint for cosmetics is churn, and no guest sees either.
  const copyCollectionLink = () => {
    if (collectionQr?.url) {
      navigator.clipboard.writeText(collectionQr.url);
      toast.success("Takeaway link copied");
    }
  };

  const downloadCollectionQr = async (fmt) => {
    if (!collectionQr?.url) return;
    try {
      const path = fmt === "pdf" ? "/menu/collection-qr.pdf" : "/menu/collection-qr.png";
      const resp = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabon-takeaway-${settings.menu_slug}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyDineInLink = () => {
    if (dineInQr?.url) {
      navigator.clipboard.writeText(dineInQr.url);
      toast.success("Dine-in link copied");
    }
  };

  const downloadDineInQr = async (fmt) => {
    if (!dineInQr?.url) return;
    try {
      const path = fmt === "pdf" ? "/menu/dine-in-qr.pdf" : "/menu/dine-in-qr.png";
      const resp = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabon-dine-in-${settings.menu_slug}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveDeliveryBonus = async () => {
    const bonus = parseInt(deliveryBonusInput, 10);
    if (isNaN(bonus) || bonus < 0 || (bonus > 0 && bonus > 500)) {
      toast.error("Bonus must be 0 (disabled) or 1–500");
      return;
    }
    try {
      await api.put("/menu/settings", { delivery_welcome_bonus: bonus });
      const { data } = await api.get("/menu/delivery-qr");
      setDeliveryQr(data);
      toast.success(bonus > 0 ? "Delivery QR activated" : "Delivery QR disabled");
    } catch (err) { toast.error(formatError(err)); }
  };

  const downloadQr = async (fmt) => {
    try {
      const path = fmt === "pdf" ? "/menu/qr.pdf" : "/menu/qr.png";
      const resp = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabon-menu-${settings.menu_slug}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  // ---- Categories ----
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post("/menu/categories", {
        name: newCatName.trim(),
        main_category_id: newCatMainCategoryId || undefined,
      });
      setNewCatName("");
      setNewCatMainCategoryId("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const patchCategory = async (id, patch) => {
    try {
      await api.put(`/menu/categories/${id}`, patch);
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeCategory = async (id) => {
    if (!window.confirm("Delete this category and ALL its items?")) return;
    try {
      await api.delete(`/menu/categories/${id}`);
      await load();
      toast.success("Category removed");
    } catch (err) { toast.error(formatError(err)); }
  };

  const startEditCat = (c) => {
    setEditingCat(c.id);
    setEditCatForm({ name: c.name, description: c.description || "", main_category_id: c.main_category_id || "" });
  };

  const saveEditCat = async (id) => {
    try {
      await api.put(`/menu/categories/${id}`, {
        name: editCatForm.name.trim() || undefined,
        description: editCatForm.description.trim() || null,
        main_category_id: editCatForm.main_category_id || null,
      });
      setEditingCat(null);
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadCatImage = async (cid, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    setCatImgUploading(s => new Set([...s, cid]));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/menu/categories/${cid}/image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Category image saved (${data.size_kb} KB)`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setCatImgUploading(s => { const ns = new Set(s); ns.delete(cid); return ns; }); }
  };

  const removeCatImage = async (cid) => {
    try {
      await api.delete(`/menu/categories/${cid}/image`);
      toast.success("Category image removed");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const moveCategory = async (id, dir) => {
    const sorted = [...categories].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((c) => c.id === id);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    await Promise.all([
      api.put(`/menu/categories/${a.id}`, { display_order: b.display_order }),
      api.put(`/menu/categories/${b.id}`, { display_order: a.display_order }),
    ]);
    await load();
  };

  // ---- Main Categories ----
  const addMainCategory = async () => {
    if (!newMainCatName.trim()) return;
    try {
      await api.post("/menu/main-categories", { name: newMainCatName.trim() });
      setNewMainCatName("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveEditMC = async (id) => {
    if (!editMCName.trim()) return;
    try {
      await api.put(`/menu/main-categories/${id}`, { name: editMCName.trim() });
      setEditingMC(null);
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeMainCategory = async (id) => {
    if (!window.confirm("Delete this main category? All sub-categories must be unassigned first.")) return;
    try {
      await api.delete(`/menu/main-categories/${id}`);
      await load();
      toast.success("Main category removed");
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadMCImage = async (mcid, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    setMCImgUploading(s => new Set([...s, mcid]));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/menu/main-categories/${mcid}/image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Main category image saved (${data.size_kb} KB)`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setMCImgUploading(s => { const ns = new Set(s); ns.delete(mcid); return ns; }); }
  };

  const removeMCImage = async (mcid) => {
    try {
      await api.delete(`/menu/main-categories/${mcid}/image`);
      toast.success("Main category image removed");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const moveMainCategory = async (id, dir) => {
    const sorted = [...mainCategories].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((mc) => mc.id === id);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    await Promise.all([
      api.put(`/menu/main-categories/${a.id}`, { display_order: b.display_order }),
      api.put(`/menu/main-categories/${b.id}`, { display_order: a.display_order }),
    ]);
    await load();
  };

  const itemsByCategory = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.id] = []; });
    items.forEach((it) => { if (m[it.category_id]) m[it.category_id].push(it); });
    return m;
  }, [categories, items]);

  const isFiltering = itemSearch.trim() !== "" || menuFilters.length > 0;

  // Items visible per category after applying search + filter chips.
  // Never mutates itemsByCategory.
  const visibleItemsByCategory = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    const passesFilters = (it) => {
      for (const f of menuFilters) {
        if (f === "sold_out" && it.is_available !== false) return false;
        if (f === "no_photo" && it.image_url) return false;
        if (f === "no_desc" && it.description && it.description.trim()) return false;
      }
      return true;
    };
    const result = {};
    categories.forEach((c) => {
      const catItems = itemsByCategory[c.id] || [];
      if (!q) { result[c.id] = catItems.filter(passesFilters); return; }
      const catNameMatch = (c.name || "").toLowerCase().includes(q);
      const matched = catNameMatch
        ? catItems // category name matches → show all its items…
        : catItems.filter((it) =>
            ((it.name || "").toLowerCase().includes(q)) ||
            ((it.description || "").toLowerCase().includes(q))
          );
      result[c.id] = matched.filter(passesFilters); // …still subject to filter chips
    });
    return result;
  }, [categories, itemsByCategory, itemSearch, menuFilters]);

  const totalItems = items.length;
  const matchedCount = useMemo(
    () => Object.values(visibleItemsByCategory).reduce((n, arr) => n + arr.length, 0),
    [visibleItemsByCategory]
  );

  // Default collapse: computed ONCE when categories first load. >5 → all collapsed.
  useEffect(() => {
    if (!collapseInitRef.current && categories.length > 0) {
      collapseInitRef.current = true;
      if (categories.length > 5) setCollapsedCats(new Set(categories.map((c) => c.id)));
    }
  }, [categories]);

  // Keep the sticky bar pinned just below the AppLayout header.
  useEffect(() => {
    const measure = () => {
      let h = 0;
      document.querySelectorAll("header").forEach((el) => {
        h = Math.max(h, el.getBoundingClientRect().height);
      });
      setStickyTop(h || 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // When an item is saved, reveal + highlight it (expand its category if collapsed).
  useEffect(() => {
    if (!highlightItemId) return;
    const it = items.find((x) => x.id === highlightItemId);
    if (it) {
      setCollapsedCats((prev) => {
        if (!prev.has(it.category_id)) return prev;
        const next = new Set(prev);
        next.delete(it.category_id);
        return next;
      });
    }
    const scrollT = setTimeout(() => {
      const el = document.querySelector(`[data-testid="item-card-${highlightItemId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const clearT = setTimeout(() => setHighlightItemId(null), 2500);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
  }, [highlightItemId, items]);

  const toggleCollapse = (id) => setCollapsedCats((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleFilter = (f) => setMenuFilters((prev) =>
    prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
  );
  const clearAll = () => { setItemSearch(""); setMenuFilters([]); };
  const expandAll = () => setCollapsedCats(new Set());
  const collapseAllCats = () => setCollapsedCats(new Set(categories.map((c) => c.id)));

  // Reorder mode is incompatible with an active search/filter — dragging a
  // filtered subset would write meaningless positions — so exit it when filtering.
  useEffect(() => { if (isFiltering) setReorderingCat(null); }, [isFiltering]);

  const toggleReorder = (catId) => {
    const entering = reorderingCat !== catId;
    setReorderingCat(entering ? catId : null);
    if (entering) {
      // Expand the category so its list is visible while reordering.
      setCollapsedCats((cs) => { if (!cs.has(catId)) return cs; const n = new Set(cs); n.delete(catId); return n; });
    }
  };

  const reorderCategory = async (categoryId, orderedIds) => {
    const prevItems = items;
    const byId = new Map(items.map((it) => [it.id, it]));
    const reordered = orderedIds.map((id, i) => ({ ...byId.get(id), display_order: i + 1 }));
    const others = items.filter((it) => it.category_id !== categoryId);
    setItems([...others, ...reordered]); // optimistic (grouping reads array order)
    try {
      await api.put("/menu/items/reorder", { category_id: categoryId, ordered_ids: orderedIds });
    } catch (err) {
      setItems(prevItems); // revert
      toast.error(formatError(err));
    }
  };

  const moveToTop = (categoryId, itemId) => {
    const current = (itemsByCategory[categoryId] || []).map((it) => it.id);
    if (current[0] === itemId) return; // already at top
    reorderCategory(categoryId, [itemId, ...current.filter((id) => id !== itemId)]);
  };

  if (!settings) return <AppLayout><div className="text-[#A0A0A0] p-6">Loading…</div></AppLayout>;

  const loyaltyRate = settings.loyalty_rate_per_100 || 10;
  const pointsRatePct = loyaltyRate;
  // An item is POS-owned (locked) only while the venue is actively syncing a POS.
  // Mirrors the backend guarantee in update_item — see /menu/settings.
  const posSyncEnabled = Boolean(settings.pos_menu_sync_enabled);
  const sortedCats = [...categories].sort((a, b) => a.display_order - b.display_order);
  const visibleCats = sortedCats.filter((c) =>
    isFiltering ? (visibleItemsByCategory[c.id] || []).length > 0 : true
  );

  return (
    <AppLayout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Menu</h1>
          <p className="text-sm text-[#A0A0A0]">Build the QR menu customers scan to see items + loyalty points.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          {/* Menu Settings */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-bold mb-3">Menu Settings</h2>
            <div className="text-xs text-[#A0A0A0] mb-3">Loyalty rate (from Venue Settings): <span className="text-[#9D00FF] font-bold">{pointsRatePct} pts / ₹100</span></div>

            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">Status</div>
              <div className="flex gap-2">
                {["draft", "published"].map((s) => (
                  <button
                    key={s}
                    data-testid={`menu-status-${s}`}
                    onClick={() => updateSettings({ menu_status: s })}
                    className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border ${
                      settings.menu_status === s
                        ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                        : "border-white/10 text-[#A0A0A0] hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">Public URL</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  data-testid="menu-public-url"
                  value={settings.public_url}
                  className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono truncate"
                />
                <button
                  onClick={copyLink}
                  data-testid="menu-copy-link"
                  className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 text-xs font-semibold"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">Menu Description (shown on public page)</div>
              <input
                value={settings.menu_description || ""}
                onChange={(e) => setSettings({ ...settings, menu_description: e.target.value })}
                onBlur={() => updateSettings({ menu_description: settings.menu_description || "" })}
                data-testid="menu-description"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                placeholder="e.g. Good Food · Good Vibes"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Disclaimers (shown at bottom of menu drawer)</div>
                {(settings.menu_disclaimers?.length || 0) < 6 && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...(settings.menu_disclaimers || []), ""];
                      setSettings({ ...settings, menu_disclaimers: next });
                    }}
                    className="text-[10px] text-[#9D00FF] hover:underline"
                  >
                    + Add line
                  </button>
                )}
              </div>
              {(settings.menu_disclaimers || []).map((d, i) => (
                <div key={i} className="flex gap-1.5 mb-1.5">
                  <input
                    maxLength={120}
                    value={d}
                    onChange={e => {
                      const next = [...settings.menu_disclaimers];
                      next[i] = e.target.value;
                      setSettings({ ...settings, menu_disclaimers: next });
                    }}
                    onBlur={() => updateSettings({ menu_disclaimers: settings.menu_disclaimers })}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
                    placeholder={`Disclaimer line ${i + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = settings.menu_disclaimers.filter((_, j) => j !== i);
                      setSettings({ ...settings, menu_disclaimers: next });
                      updateSettings({ menu_disclaimers: next });
                    }}
                    className="text-[#A0A0A0] hover:text-red-400"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {(settings.menu_disclaimers || []).length === 0 && (
                <div className="text-[11px] text-[#A0A0A0]">No disclaimers added.</div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">"New" badge window (days)</div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={7}
                  max={60}
                  value={settings.menu_new_badge_days ?? 21}
                  onChange={e => setSettings({ ...settings, menu_new_badge_days: Number(e.target.value) })}
                  onBlur={() => {
                    const days = Number(settings.menu_new_badge_days);
                    if (days >= 7 && days <= 60) updateSettings({ menu_new_badge_days: days });
                    else toast.error("New badge window must be 7–60 days");
                  }}
                  className="w-24 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
                <span className="text-[11px] text-[#A0A0A0]">Items marked New show the badge for this many days (7–60)</span>
              </div>
            </div>
          </div>

          {/* QR */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col items-center">
            <h2 className="text-lg font-bold mb-3 self-start">QR Code</h2>
            <div className="flex flex-col items-center">
              <div className="bg-white p-3 rounded-xl mb-4 flex items-center justify-center" style={{ width: 160, height: 160 }}>
                <QrCode size={110} className="text-black" />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => downloadQr("png")}
                  data-testid="menu-qr-png"
                  className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-3 py-2 text-xs font-semibold"
                >
                  <Download size={13} /> PNG
                </button>
                <button
                  onClick={() => downloadQr("pdf")}
                  data-testid="menu-qr-pdf"
                  className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold"
                >
                  <Download size={13} /> PDF
                </button>
              </div>
              <div className="text-[10px] text-[#A0A0A0] mt-3 text-center max-w-[280px]">
                The QR always encodes the same URL, so it never changes when you update your menu.
                {dineInQr?.active && collectionQr?.active &&
                  " This one asks the guest whether they're dining in or ordering takeaway — for tables, print the Dine-in QR below; for pickup, the Takeaway QR."}
              </div>
            </div>
          </div>

          {/* Delivery Registration QR */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Delivery Registration QR</h2>
              {deliveryQr?.active ? (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1">
                  Active — {deliveryQr.bonus} bonus pts
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-full px-3 py-1">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-[11px] text-[#A0A0A0]">
              Customers scan this to join your loyalty programme. Set bonus points to activate.
            </p>
            {!deliveryQr?.active && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                Set a bonus below to activate delivery registration
              </div>
            )}
            {deliveryQr?.url && (
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={deliveryQr.url}
                  className={`flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono ${!deliveryQr.active ? "opacity-50" : ""}`}
                />
                <button
                  onClick={copyDeliveryLink}
                  disabled={!deliveryQr.active}
                  className="flex items-center gap-1 bg-white/5 hover:bg-white/10 disabled:opacity-40 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                >
                  <Copy size={12} /> Copy
                </button>
                <button
                  onClick={downloadDeliveryQr}
                  disabled={!deliveryQr.active}
                  className="flex items-center gap-1 bg-white/5 hover:bg-white/10 disabled:opacity-40 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                >
                  <Download size={12} /> QR
                </button>
              </div>
            )}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-[#A0A0A0] mb-1 block">
                Bonus points for new registrations
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="500"
                  value={deliveryBonusInput}
                  onChange={(e) => setDeliveryBonusInput(e.target.value)}
                  className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
                <button
                  onClick={saveDeliveryBonus}
                  className="bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-2 text-xs font-semibold"
                >
                  Save
                </button>
              </div>
              <p className="text-[10px] text-[#A0A0A0] mt-1">
                Minimum 1 point to activate. Set to 0 to disable.
              </p>
            </div>
          </div>
        </div>

          {/* Room Service QR */}
          {roomServiceQr && (
            <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col gap-3 ${!roomServiceQr.active || settings?.menu_status !== "published" ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Room Service QR</h2>
                {roomServiceQr.active ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1">
                    Active
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-full px-3 py-1">
                    Inactive
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#A0A0A0]">
                Guests scan this QR in their room to order via room service. Enable room service in Settings first.
              </p>
              {!roomServiceQr.active && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                  Enable room service in Settings → Ordering to activate this QR
                </div>
              )}
              {settings?.menu_status !== "published" && roomServiceQr.active && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                  Publish your menu first to make the QR link work
                </div>
              )}
              {roomServiceQr.url && (
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={roomServiceQr.url}
                    className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono"
                  />
                  <button
                    onClick={copyRoomServiceLink}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Copy size={12} /> Copy
                  </button>
                  <button
                    onClick={downloadRoomServiceQr}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Download size={12} /> QR
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Dine-in QR (AD-3) — only when the venue takes dine-in orders (ordering_mode dine_in or
              both, reported as active). The link for table tents, so the seated guest's menu opens
              scoped to dine-in instead of the unscoped QR's "choose how you're ordering". */}
          {dineInQr?.active && (
            <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col gap-3 ${settings?.menu_status !== "published" ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Dine-in QR</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1">
                  Dine-in
                </span>
              </div>
              <p className="text-[11px] text-[#A0A0A0]">
                Print this for your tables. It opens the menu already scoped to dine-in, so a seated guest sees the full menu — including dishes not offered for takeaway.
              </p>
              {settings?.menu_status !== "published" && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                  Publish your menu first to make the QR link work
                </div>
              )}
              {dineInQr.url && (
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={dineInQr.url}
                    className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono"
                  />
                  <button
                    onClick={copyDineInLink}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Copy size={12} /> Copy
                  </button>
                  <button
                    onClick={() => downloadDineInQr("png")}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Download size={12} /> PNG
                  </button>
                  <button
                    onClick={() => downloadDineInQr("pdf")}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Download size={12} /> PDF
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Takeaway QR (AC-3; renamed from "Collection" in AE-2) — only when the venue takes takeaway
              orders (ordering_mode takeaway or both, which the endpoint reports as active). The API path
              and state var keep the "collection" name; only the label a venue admin sees is "Takeaway". */}
          {collectionQr?.active && (
            <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col gap-3 ${settings?.menu_status !== "published" ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Takeaway QR</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1">
                  Takeaway
                </span>
              </div>
              <p className="text-[11px] text-[#A0A0A0]">
                Share this for pickup orders. It opens the menu already scoped to takeaway, so food marked dine-in only never shows and can't be added.
              </p>
              {settings?.menu_status !== "published" && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                  Publish your menu first to make the QR link work
                </div>
              )}
              {collectionQr.url && (
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={collectionQr.url}
                    className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono"
                  />
                  <button
                    onClick={copyCollectionLink}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Copy size={12} /> Copy
                  </button>
                  <button
                    onClick={() => downloadCollectionQr("png")}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Download size={12} /> PNG
                  </button>
                  <button
                    onClick={() => downloadCollectionQr("pdf")}
                    className="flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold shrink-0"
                  >
                    <Download size={12} /> PDF
                  </button>
                </div>
              )}
            </div>
          )}

        {/* Appearance (branding) */}
        <AppearanceSection settings={settings} onSaved={load} />

        {/* Main Categories */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-bold mb-1">Main Categories</h2>
          <p className="text-xs text-[#A0A0A0] mb-3">Top-level navigation on your public menu. Sub-categories live inside each main category.</p>
          <div className="flex gap-2 mb-4">
            <input
              value={newMainCatName}
              onChange={(e) => setNewMainCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addMainCategory()}
              placeholder="New main category name (e.g. Food, Bar)"
              data-testid="menu-new-mc-input"
              className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
            <button
              onClick={addMainCategory}
              data-testid="menu-add-mc"
              className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 text-sm font-semibold"
            >
              <Plus size={14} /> Add
            </button>
          </div>

          <div className="flex flex-col divide-y divide-white/5" data-testid="menu-mc-list">
            {[...mainCategories].sort((a, b) => a.display_order - b.display_order).map((mc, i) => (
              <div key={mc.id} className="py-2.5">
                <div className="flex items-center gap-2.5">
                  {/* Reorder arrows */}
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => moveMainCategory(mc.id, "up")} disabled={i === 0} className="text-[#A0A0A0] disabled:opacity-30"><ArrowUp size={12} /></button>
                    <button onClick={() => moveMainCategory(mc.id, "down")} disabled={i === mainCategories.length - 1} className="text-[#A0A0A0] disabled:opacity-30"><ArrowDown size={12} /></button>
                  </div>

                  {/* Image thumbnail */}
                  {mc.image_url && (
                    <img src={resolveImgUrl(mc.image_url)} alt={mc.name}
                      className="w-9 h-9 rounded object-cover border border-white/10 shrink-0" />
                  )}

                  {/* Name + sub-cat count */}
                  <div className="flex-1 min-w-0">
                    {editingMC === mc.id ? (
                      <input
                        autoFocus
                        value={editMCName}
                        onChange={e => setEditMCName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEditMC(mc.id); if (e.key === "Escape") setEditingMC(null); }}
                        className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1 text-sm text-white w-full max-w-xs"
                      />
                    ) : (
                      <div className="font-semibold text-sm">{mc.name}</div>
                    )}
                    <div className="text-[11px] text-[#A0A0A0] mt-0.5">
                      {categories.filter(c => c.main_category_id === mc.id).length} sub-categor
                      {categories.filter(c => c.main_category_id === mc.id).length === 1 ? "y" : "ies"}
                    </div>
                  </div>

                  {/* Active toggle */}
                  <label className="flex items-center gap-1 text-[11px] text-[#A0A0A0] cursor-pointer shrink-0">
                    <input type="checkbox" checked={mc.is_active}
                      onChange={() => api.put(`/menu/main-categories/${mc.id}`, { is_active: !mc.is_active }).then(load)}
                      className="accent-[#9D00FF]" />
                    Active
                  </label>

                  {/* Image upload */}
                  <label className="cursor-pointer shrink-0 text-[#A0A0A0] hover:text-white" title="Upload main category photo">
                    {mcImgUploading.has(mc.id) ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                    <input type="file" accept="image/*" className="sr-only"
                      onChange={e => { uploadMCImage(mc.id, e.target.files[0]); e.target.value = ""; }} />
                  </label>

                  {/* Edit pencil */}
                  {editingMC === mc.id ? (
                    <>
                      <button onClick={() => saveEditMC(mc.id)} className="text-emerald-400 hover:text-emerald-300 shrink-0"><Check size={14} /></button>
                      <button onClick={() => setEditingMC(null)} className="text-[#A0A0A0] hover:text-white shrink-0"><X size={14} /></button>
                    </>
                  ) : (
                    <button onClick={() => { setEditingMC(mc.id); setEditMCName(mc.name); }}
                      className="text-[#A0A0A0] hover:text-white shrink-0"><Pencil size={14} /></button>
                  )}

                  {/* Delete */}
                  <button onClick={() => removeMainCategory(mc.id)} className="text-[#EF4444] hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
                </div>

                {/* Image management when editing */}
                {editingMC === mc.id && mc.image_url && (
                  <div className="mt-2 ml-7 flex items-center gap-3">
                    <img src={resolveImgUrl(mc.image_url)} alt="Preview"
                      className="w-16 h-12 rounded-lg object-cover border border-white/10" />
                    <button type="button" onClick={() => removeMCImage(mc.id)}
                      className="text-xs text-red-400 hover:underline">Remove image</button>
                  </div>
                )}
              </div>
            ))}
            {mainCategories.length === 0 && (
              <div className="py-4 text-sm text-[#A0A0A0]">No main categories yet. Add your first one above.</div>
            )}
          </div>
        </div>

        {/* Sub-categories */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-bold mb-1">Sub-Categories</h2>
          <p className="text-xs text-[#A0A0A0] mb-3">Items are grouped inside sub-categories. Assign each to a main category.</p>
          <div className="flex gap-2 mb-4 flex-wrap">
            <input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder="New sub-category name"
              data-testid="menu-new-cat-input"
              className="flex-1 min-w-[160px] bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
            <select
              value={newCatMainCategoryId}
              onChange={(e) => setNewCatMainCategoryId(e.target.value)}
              data-testid="menu-new-cat-mc"
              className="w-44 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">No main category</option>
              {mainCategories.map(mc => <option key={mc.id} value={mc.id}>{mc.name}</option>)}
            </select>
            <button
              onClick={addCategory}
              data-testid="menu-add-cat"
              className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 text-sm font-semibold"
            >
              <Plus size={14} /> Add
            </button>
          </div>

          <div className="flex flex-col divide-y divide-white/5" data-testid="menu-cat-list">
            {[...categories].sort((a, b) => a.display_order - b.display_order).map((c, i) => (
              <div key={c.id} className="py-2.5">
                {/* ─ Row ─ */}
                <div className="flex items-center gap-2.5">
                  {/* Reorder arrows */}
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => moveCategory(c.id, "up")} disabled={i === 0} className="text-[#A0A0A0] disabled:opacity-30"><ArrowUp size={12} /></button>
                    <button onClick={() => moveCategory(c.id, "down")} disabled={i === categories.length - 1} className="text-[#A0A0A0] disabled:opacity-30"><ArrowDown size={12} /></button>
                  </div>

                  {/* Image thumbnail (if exists) */}
                  {c.image_url && (
                    <img
                      src={resolveImgUrl(c.image_url)}
                      alt={c.name}
                      className="w-9 h-9 rounded object-cover border border-white/10 shrink-0"
                    />
                  )}

                  {/* Name + subtitle */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    {(c.description || c.main_category_id) && (
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {c.main_category_id && (() => {
                          const mc = mainCategories.find(m => m.id === c.main_category_id);
                          return mc ? (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-[#9D00FF]/15 text-[#9D00FF] rounded px-1.5 py-0.5">
                              {mc.name}
                            </span>
                          ) : null;
                        })()}
                        {c.description && (
                          <span className="text-[11px] text-[#A0A0A0] truncate">{c.description}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Active toggle */}
                  <label className="flex items-center gap-1 text-[11px] text-[#A0A0A0] cursor-pointer shrink-0">
                    <input
                      type="checkbox" checked={c.is_active}
                      onChange={() => patchCategory(c.id, { is_active: !c.is_active })}
                      className="accent-[#9D00FF]"
                    />
                    Active
                  </label>

                  {/* Image upload */}
                  <label className="cursor-pointer shrink-0 text-[#A0A0A0] hover:text-white" title="Upload category photo">
                    {catImgUploading.has(c.id)
                      ? <Loader2 size={14} className="animate-spin" />
                      : <ImageIcon size={14} />}
                    <input
                      type="file" accept="image/*" className="sr-only"
                      onChange={e => { uploadCatImage(c.id, e.target.files[0]); e.target.value = ""; }}
                    />
                  </label>

                  {/* Edit pencil */}
                  <button
                    onClick={() => editingCat === c.id ? setEditingCat(null) : startEditCat(c)}
                    className={`shrink-0 ${editingCat === c.id ? "text-[#9D00FF]" : "text-[#A0A0A0] hover:text-white"}`}
                    title="Edit details"
                  >
                    <Pencil size={14} />
                  </button>

                  {/* Delete */}
                  <button onClick={() => removeCategory(c.id)} className="text-[#EF4444] hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
                </div>

                {/* ─ Inline edit form (expanded when editingCat === c.id) ─ */}
                {editingCat === c.id && (
                  <div className="mt-2.5 ml-7 bg-[#0A0A0A] border border-white/10 rounded-xl p-3 flex flex-col gap-2.5">
                    {/* Name */}
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0] mb-1">Category Name</div>
                      <input
                        autoFocus
                        value={editCatForm.name}
                        onChange={e => setEditCatForm(f => ({ ...f, name: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && saveEditCat(c.id)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white"
                        placeholder="e.g. Starters"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0] mb-1">
                        Description <span className="normal-case font-normal">(max 60 chars — shown on public menu card)</span>
                      </div>
                      <input
                        value={editCatForm.description}
                        onChange={e => setEditCatForm(f => ({ ...f, description: e.target.value.slice(0, 60) }))}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white"
                        placeholder="e.g. Indian favourites · Chef's specials"
                        maxLength={60}
                      />
                      <div className="text-right text-[10px] text-[#A0A0A0] mt-0.5">
                        {editCatForm.description.length}/60
                      </div>
                    </div>

                    {/* Main Category assignment */}
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0] mb-1">
                        Main Category <span className="normal-case font-normal">(top-level group this sub-category belongs to)</span>
                      </div>
                      <select
                        value={editCatForm.main_category_id}
                        onChange={e => setEditCatForm(f => ({ ...f, main_category_id: e.target.value }))}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white"
                      >
                        <option value="">— No main category —</option>
                        {mainCategories.map(mc => (
                          <option key={mc.id} value={mc.id}>{mc.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Image upload area */}
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">Category Photo (4:3, auto-cropped to WebP)</div>
                      {c.image_url ? (
                        <div className="flex items-center gap-3">
                          <img src={resolveImgUrl(c.image_url)} alt="Preview"
                            className="w-16 h-12 rounded-lg object-cover border border-white/10" />
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-[#9D00FF] cursor-pointer hover:underline">
                              Replace
                              <input type="file" accept="image/*" className="sr-only"
                                onChange={e => { uploadCatImage(c.id, e.target.files[0]); e.target.value = ""; }} />
                            </label>
                            <button type="button" onClick={() => removeCatImage(c.id)}
                              className="text-xs text-red-400 hover:underline text-left">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-white/15 hover:border-[#9D00FF]/50 rounded-lg p-3 transition group">
                          {catImgUploading.has(c.id)
                            ? <Loader2 size={14} className="animate-spin text-[#9D00FF]" />
                            : <Camera size={14} className="text-[#A0A0A0] group-hover:text-[#9D00FF] transition" />}
                          <span className="text-xs text-[#A0A0A0] group-hover:text-white transition">Upload Photo</span>
                          <input type="file" accept="image/*" className="sr-only"
                            onChange={e => { uploadCatImage(c.id, e.target.files[0]); e.target.value = ""; }} />
                        </label>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => setEditingCat(null)}
                        className="border border-white/10 rounded-lg px-3 py-1.5 text-xs text-[#A0A0A0] hover:text-white">
                        Cancel
                      </button>
                      <button type="button" onClick={() => saveEditCat(c.id)}
                        className="bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-1.5 text-xs font-semibold text-white">
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {categories.length === 0 && <div className="py-4 text-sm text-[#A0A0A0]">No sub-categories yet. Add your first one above.</div>}
          </div>
        </div>

        {/* Add-on groups */}
        <AddonGroupsSection groups={addonGroups} onReload={reloadAddonGroups} />

        {/* Sticky search + filter bar */}
        <div
          className="sticky z-30 bg-[#1A1A1A] border border-white/10 rounded-xl p-3 mb-4"
          style={{ top: stickyTop }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
              <input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Search items, descriptions, or a category…"
                data-testid="menu-item-search"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:outline-none"
              />
              {itemSearch && (
                <button type="button" onClick={() => setItemSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
            {[
              { k: "sold_out", label: "Sold out" },
              { k: "no_photo", label: "No photo" },
              { k: "no_desc", label: "No description" },
            ].map(({ k, label }) => (
              <button key={k} type="button" onClick={() => toggleFilter(k)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                  menuFilters.includes(k)
                    ? "bg-[#9D00FF]/20 text-white border-[#9D00FF]"
                    : "bg-white/5 text-[#A0A0A0] border-white/10 hover:border-white/30"
                }`}>
                {label}
              </button>
            ))}
            <div className="flex items-center gap-2 ml-auto">
              <button type="button" onClick={() => navigate("/menu/item-codes")}
                className="flex items-center gap-1 text-xs font-semibold text-[#9D00FF] hover:text-[#B94DFF]">
                <Hash size={13} /> Item codes
              </button>
              <span className="text-[#333]">·</span>
              <button type="button" onClick={() => navigate("/menu/availability")}
                className="flex items-center gap-1 text-xs font-semibold text-[#9D00FF] hover:text-[#B94DFF]">
                <MapPin size={13} /> Order types
              </button>
              {/* Tax groups bulk editor offered only when the venue has more than one group — while
                  constrained to food (AA-2) it would set everything to the one group it can have. The
                  page and endpoint stay; the link returns with multi-rate invoicing. */}
              {(user?.venue?.tax_table?.length || 1) > 1 && (
                <>
                  <span className="text-[#333]">·</span>
                  <button type="button" onClick={() => navigate("/menu/tax-groups")}
                    className="flex items-center gap-1 text-xs font-semibold text-[#9D00FF] hover:text-[#B94DFF]">
                    <Percent size={13} /> Tax groups
                  </button>
                </>
              )}
              <span className="text-[#333]">·</span>
              <button type="button" onClick={expandAll} className="text-xs text-[#A0A0A0] hover:text-white">Expand all</button>
              <span className="text-[#333]">·</span>
              <button type="button" onClick={collapseAllCats} className="text-xs text-[#A0A0A0] hover:text-white">Collapse all</button>
            </div>
          </div>
          {isFiltering && (
            <div className="flex items-center gap-3 mt-2 text-xs text-[#A0A0A0]">
              <span>{matchedCount} of {totalItems} items</span>
              <button type="button" onClick={clearAll} className="text-[#9D00FF] font-semibold hover:underline">Clear all</button>
            </div>
          )}
        </div>

        {/* Items by Category */}
        <div className="flex flex-col gap-4">
          {visibleCats.map((c) => {
            const showReorder = !isFiltering && reorderingCat === c.id;
            const collapsed = !isFiltering && !showReorder && collapsedCats.has(c.id);
            const shown = isFiltering ? (visibleItemsByCategory[c.id] || []) : (itemsByCategory[c.id] || []);
            const catItems = itemsByCategory[c.id] || [];
            return (
              <div key={c.id} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <button type="button" onClick={() => toggleCollapse(c.id)}
                    className="flex items-center gap-2 min-w-0 text-left">
                    {collapsed
                      ? <ChevronRight size={16} className="text-[#A0A0A0] shrink-0" />
                      : <ChevronDown size={16} className="text-[#A0A0A0] shrink-0" />}
                    <h3 className="text-lg font-bold truncate">{c.name} <span className="text-xs text-[#A0A0A0] ml-2 font-normal">({catItems.length} items)</span></h3>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    {catItems.length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleReorder(c.id); }}
                        disabled={isFiltering}
                        title={isFiltering ? "Clear search and filters to reorder" : "Drag items to reorder"}
                        className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                          showReorder ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "bg-white/5 hover:bg-white/10 border-white/10"
                        }`}
                      >
                        <ListOrdered size={12} /> {showReorder ? "Done" : "Reorder"}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setItemModal({ mode: "create", categoryId: c.id }); }}
                      data-testid={`menu-add-item-${c.id}`}
                      className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-xs font-semibold"
                    >
                      <Plus size={12} /> Add Item
                    </button>
                  </div>
                </div>
                {showReorder ? (
                  <ReorderList items={catItems} onReorder={(ids) => reorderCategory(c.id, ids)} />
                ) : !collapsed ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {shown.map((it) => (
                      <ItemCard
                        key={it.id}
                        item={it}
                        pointsRate={loyaltyRate}
                        posOwned={posSyncEnabled && Boolean(it.pos_item_id)}
                        isHighlighted={it.id === highlightItemId}
                        onEdit={() => setItemModal({ mode: "edit", categoryId: c.id, item: it })}
                        onRefresh={load}
                        onMoveToTop={() => moveToTop(c.id, it.id)}
                      />
                    ))}
                    {shown.length === 0 && (
                      <div className="col-span-full text-xs text-[#A0A0A0]">No items yet.</div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
          {isFiltering && matchedCount === 0 && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-8 text-center">
              <p className="text-sm text-[#A0A0A0] mb-2">No items match your search.</p>
              <button type="button" onClick={clearAll} className="text-[#9D00FF] font-semibold text-sm hover:underline">Clear all</button>
            </div>
          )}
        </div>
      </div>

      {itemModal && (
        <ItemModal
          state={itemModal}
          categories={categories}
          allItems={items}
          pointsRate={loyaltyRate}
          posSyncEnabled={posSyncEnabled}
          addonGroups={addonGroups}
          onAddonGroupsChanged={reloadAddonGroups}
          hasBottleLocker={user?.venue?.features?.bottle_locker === true}
          onClose={() => setItemModal(null)}
          onSaved={async (savedId) => { setItemModal(null); await load(); if (savedId) setHighlightItemId(savedId); }}
        />
      )}
    </AppLayout>
  );
}

// ── Drag-to-reorder (Phase 2) ───────────────────────────────────────────────
function SortableItemRow({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const typ = ITEM_TYPES.find((t) => t.v === item.item_type) || ITEM_TYPES[0];
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 ${!item.is_available ? "opacity-60" : ""}`}>
      <button type="button" {...attributes} {...listeners}
        aria-label="Drag to reorder"
        className="cursor-grab active:cursor-grabbing text-[#A0A0A0] hover:text-white touch-none shrink-0">
        <GripVertical size={16} />
      </button>
      <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: typ.dot }} />
      <span className="text-sm text-white truncate flex-1">{item.name}</span>
      {!item.is_available && <span className="text-[9px] font-bold uppercase tracking-wider text-[#A0A0A0] shrink-0">Sold out</span>}
      {!item.has_variants && <span className="text-xs text-[#A0A0A0] shrink-0">₹{item.price}</span>}
    </div>
  );
}

function ReorderList({ items, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map((it) => it.id);
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1.5">
          {items.map((it) => <SortableItemRow key={it.id} item={it} />)}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function ItemCard({ item, pointsRate, posOwned, onEdit, onRefresh, isHighlighted, onMoveToTop }) {
  const pts = item.has_variants
    ? null
    : (item.points_override ?? Math.floor((item.price || 0) * pointsRate / 100));
  const typ = ITEM_TYPES.find((t) => t.v === item.item_type) || ITEM_TYPES[0];
  const posLabel = item.pos_provider
    ? item.pos_provider.charAt(0).toUpperCase() + item.pos_provider.slice(1)
    : "POS";
  const toggle = async (k, val) => {
    try {
      await api.put(`/menu/items/${item.id}`, { [k]: val !== undefined ? val : !item[k] });
      await onRefresh();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/menu/items/${item.id}`);
      await onRefresh();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div
      data-testid={`item-card-${item.id}`}
      className={`bg-[#0A0A0A] border rounded-lg p-3 transition-all duration-500 ${!item.is_available ? "opacity-60" : ""} ${
        isHighlighted ? "border-[#9D00FF] ring-2 ring-[#9D00FF]/60" : "border-white/10"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: typ.dot }} />
            <div className="font-bold text-sm truncate">{item.name}</div>
            {item.is_featured && <Star size={12} className="text-[#F59E0B]" />}
            {posOwned && (
              <span
                title={`Synced from ${posLabel} — name, price, variants and availability are managed there`}
                className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-[#A0A0A0] border border-white/10 shrink-0"
              >
                {posLabel}
              </span>
            )}
            {item.is_must_try && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#9D00FF]/15 text-[#D78CFF] border border-[#9D00FF]/40">Must Try</span>}
            {item.marked_new_at && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/40">New</span>}
          </div>
          {item.description && <div className="text-[11px] text-[#A0A0A0] mt-0.5 line-clamp-2">{item.description}</div>}
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {item.tags.map(tag => (
                <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded border border-white/10 text-[#A0A0A0] bg-white/5">{tag}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {item.has_variants && item.variants?.length ? (
              <span className="text-[#A0A0A0] text-xs">
                {item.variants.map(v => `${v.label} ₹${v.price}`).join(" · ")}
              </span>
            ) : (
              <>
                <span className="text-white font-bold">₹{item.price}</span>
                <span className="text-[#9D00FF] text-xs font-bold">🏆 {pts} pts</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {onMoveToTop && (
            <button onClick={onMoveToTop} title="Move to top of category" aria-label="Move to top of category"
              className="text-[#A0A0A0] hover:text-[#9D00FF]"><ArrowUpToLine size={12} /></button>
          )}
          <button onClick={onEdit} className="text-[#A0A0A0] hover:text-white" data-testid={`item-edit-${item.id}`}><Pencil size={12} /></button>
          <button onClick={remove} className="text-[#EF4444] hover:text-red-400"><Trash2 size={12} /></button>
        </div>
      </div>
      <div className="flex gap-1 mt-2 flex-wrap">
        <button
          onClick={() => toggle("is_available")}
          disabled={posOwned}
          title={posOwned ? `Availability is managed in ${posLabel} — mark it there and it updates here within seconds` : undefined}
          className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${posOwned ? "cursor-not-allowed opacity-70" : ""} ${item.is_available ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-[#A0A0A0] border-white/10"}`}
        >
          {item.is_available ? "Available" : "Sold Out"}
        </button>
        <button onClick={() => toggle("is_featured")} className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${item.is_featured ? "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40" : "bg-white/5 text-[#A0A0A0] border-white/10"}`}>
          {item.is_featured ? "Bestseller" : "Bestseller?"}
        </button>
        <button onClick={() => toggle("is_must_try")} className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${item.is_must_try ? "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40" : "bg-white/5 text-[#A0A0A0] border-white/10"}`}>
          {item.is_must_try ? "Must Try" : "Must Try?"}
        </button>
      </div>
    </div>
  );
}

const DEFAULT_VARIANTS = [
  { label: "", price: "" },
  { label: "", price: "" },
];

// ── Add-on links: presets + rule text (shared by the item editor below) ──
// A link is {group_id, min, max}. min/max live on the LINK, not the group, so the
// same group can be "choose one" on one item and "choose any" on another.
const ADDON_PRESETS = [
  { key: "choose_one", label: "Choose one (required)" },
  { key: "optional_one", label: "Choose one (optional)" },
  { key: "choose_any", label: "Choose any" },
  { key: "custom", label: "Custom…" },
];

function addonRuleLabel(min, max) {
  const mn = Number(min) || 0;
  const mx = Number(max) || 0;
  if (mn > 0 && mx > 0 && mn === mx) return `Choose ${mn}`;
  if (mn > 0 && mx > mn) return `Choose ${mn}–${mx}`;
  if (mn > 0 && mx === 0) return `Choose at least ${mn}`;
  if (mn === 0 && mx === 1) return "Optional";
  if (mn === 0 && mx > 1) return `Optional · up to ${mx}`;
  return "Optional";
}

function presetForRule(min, max, optCount) {
  const mn = Number(min) || 0;
  const mx = Number(max) || 0;
  if (mn === 1 && mx === 1) return "choose_one";
  if (mn === 0 && mx === 1) return "optional_one";
  if (mn === 0 && mx === optCount && optCount > 0) return "choose_any";
  return "custom";
}

function ruleForPreset(preset, optCount) {
  const n = Math.max(1, optCount || 1);
  switch (preset) {
    case "choose_one": return { min: 1, max: 1 };
    case "optional_one": return { min: 0, max: 1 };
    case "choose_any": return { min: 0, max: n };
    default: return null; // custom — keep whatever min/max the link already has
  }
}

// A safe default rule for a freshly attached group: optional, up to however many
// options it has (min 0 never blocks ordering). Empty group → a harmless 0/0.
function defaultAddonRule(optCount) {
  const n = optCount || 0;
  return n === 0 ? { min: 0, max: 0 } : { min: 0, max: n };
}

function ItemAddonEditor({ links, setLinks, groups, disabled, posProvider, onReloadGroups }) {
  const [attachId, setAttachId] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOptions, setNewOptions] = useState([
    { name: "", price: "", item_type: "veg" },
    { name: "", price: "", item_type: "veg" },
  ]);
  const [saving, setSaving] = useState(false);

  const byId = React.useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);
  const optCountOf = (gid) => (byId[gid]?.items?.length || 0);
  const linkedIds = new Set(links.map(l => l.group_id));
  const available = groups.filter(g => !linkedIds.has(g.id));
  const provLabel = (g) => (g?.pos_provider
    ? g.pos_provider.charAt(0).toUpperCase() + g.pos_provider.slice(1)
    : "POS");

  // Read-only view for POS-owned items — the importer manages their add-on links.
  if (disabled) {
    if (!links.length) return null;
    return (
      <div className="col-span-2 border-t border-white/10 pt-3 mt-1">
        <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Add-ons</div>
        <div className="flex flex-col gap-1.5">
          {links.map(l => {
            const g = byId[l.group_id];
            return (
              <div key={l.group_id} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 opacity-70">
                <div className="text-sm text-white truncate">{g?.name || "Add-on group"}</div>
                <div className="text-[11px] text-[#A0A0A0] shrink-0">{addonRuleLabel(l.min, l.max)}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-[#A0A0A0] mt-1.5">Add-ons for this item are managed in {posProvider}.</p>
      </div>
    );
  }

  const attach = (gid) => {
    setAttachId("");
    if (!gid || linkedIds.has(gid)) return;
    setLinks([...links, { group_id: gid, ...defaultAddonRule(optCountOf(gid)) }]);
  };
  const detach = (gid) => setLinks(links.filter(l => l.group_id !== gid));
  const setPreset = (gid, preset) => {
    const r = ruleForPreset(preset, optCountOf(gid));
    if (!r) return; // custom — leave min/max as-is for manual editing
    setLinks(links.map(l => l.group_id === gid ? { ...l, ...r } : l));
  };
  const setCustom = (gid, field, value) => {
    const n = optCountOf(gid);
    let v = value === "" ? 0 : Math.max(0, parseInt(value, 10) || 0);
    if (v > n) v = n; // a link can never require more than the group offers
    setLinks(links.map(l => l.group_id === gid ? { ...l, [field]: v } : l));
  };

  const createAndAttach = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name the add-on group"); return; }
    const items = newOptions
      .filter(o => o.name.trim())
      .map(o => ({ name: o.name.trim(), price: o.price === "" ? 0 : Number(o.price) || 0, item_type: o.item_type }));
    setSaving(true);
    try {
      const { data } = await api.post("/menu/addon-groups", { name, items });
      if (onReloadGroups) await onReloadGroups();
      setLinks([...links, { group_id: data.id, ...defaultAddonRule(items.length) }]);
      setCreating(false);
      setNewName("");
      setNewOptions([
        { name: "", price: "", item_type: "veg" },
        { name: "", price: "", item_type: "veg" },
      ]);
      toast.success("Add-on group created");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="col-span-2 border-t border-white/10 pt-3 mt-1">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Add-ons (optional)</div>

      {links.length > 0 && (
        <div className="flex flex-col gap-2 mb-2">
          {links.map(l => {
            const g = byId[l.group_id];
            const n = optCountOf(l.group_id);
            const preset = presetForRule(l.min, l.max, n);
            const isPos = Boolean(g?.pos_group_id);
            return (
              <div key={l.group_id} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {g?.name || "Add-on group"}
                      {isPos && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-[#A0A0A0]">· {provLabel(g)}</span>}
                    </div>
                    <div className="text-[11px] text-[#A0A0A0]">
                      {n} option{n === 1 ? "" : "s"} · {addonRuleLabel(l.min, l.max)}
                    </div>
                  </div>
                  <button type="button" onClick={() => detach(l.group_id)} className="text-[#A0A0A0] hover:text-white shrink-0" aria-label="Remove add-on group">
                    <X size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <select value={preset} onChange={e => setPreset(l.group_id, e.target.value)}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                    {ADDON_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                  {preset === "custom" && (
                    <div className="flex items-center gap-1.5 text-xs text-[#A0A0A0]">
                      <span>min</span>
                      <input type="number" min={0} max={n} value={l.min}
                        onChange={e => setCustom(l.group_id, "min", e.target.value)}
                        className="w-14 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-white" />
                      <span>max</span>
                      <input type="number" min={0} max={n} value={l.max}
                        onChange={e => setCustom(l.group_id, "max", e.target.value)}
                        className="w-14 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-white" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {available.length > 0 && (
        <select value={attachId} onChange={e => attach(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-2">
          <option value="">+ Attach an existing add-on group…</option>
          {available.map(g => {
            const n = g.items?.length || 0;
            return (
              <option key={g.id} value={g.id} disabled={n === 0}>
                {g.name} · {n} option{n === 1 ? "" : "s"}{g.pos_group_id ? " (POS)" : ""}{n === 0 ? " — add options first" : ""}
              </option>
            );
          })}
        </select>
      )}

      {!creating ? (
        <button type="button" onClick={() => setCreating(true)} className="text-xs text-[#D78CFF] hover:text-white">
          + Create a new add-on group
        </button>
      ) : (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex flex-col gap-2.5">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0] mb-1">Group name</div>
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Choose your sauce"
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white" />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0]">Options <span className="normal-case font-normal">(₹0 is fine — most are free)</span></div>
            {newOptions.map((o, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <input value={o.name} onChange={e => {
                  const next = [...newOptions]; next[idx] = { ...o, name: e.target.value }; setNewOptions(next);
                }} placeholder={`Option ${idx + 1}`}
                  className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white" />
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[#A0A0A0] text-xs">₹</span>
                  <input type="number" min={0} value={o.price} onChange={e => {
                    const next = [...newOptions]; next[idx] = { ...o, price: e.target.value }; setNewOptions(next);
                  }} placeholder="0"
                    className="w-16 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
                </div>
                <select value={o.item_type} onChange={e => {
                  const next = [...newOptions]; next[idx] = { ...o, item_type: e.target.value }; setNewOptions(next);
                }} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-1.5 py-1.5 text-xs text-white shrink-0">
                  <option value="veg">Veg</option>
                  <option value="nonveg">Non-veg</option>
                </select>
                {newOptions.length > 1 && (
                  <button type="button" onClick={() => setNewOptions(newOptions.filter((_, i) => i !== idx))}
                    className="text-[#A0A0A0] hover:text-white text-lg leading-none shrink-0" aria-label="Remove option">×</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setNewOptions([...newOptions, { name: "", price: "", item_type: "veg" }])}
              className="self-start text-xs text-[#D78CFF] hover:text-white">+ Add option</button>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)}
              className="border border-white/10 rounded-lg px-3 py-1.5 text-xs text-[#A0A0A0] hover:text-white">Cancel</button>
            <button type="button" onClick={createAndAttach} disabled={saving}
              className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-4 py-1.5 text-xs font-semibold text-white">
              {saving ? "Creating…" : "Create & attach"}
            </button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-[#A0A0A0] mt-1.5">Add-ons let guests customise this item (sauces, sides, spice level). Manage every group in the Add-ons section on the menu page.</p>
    </div>
  );
}

function ItemModal({ state, categories, allItems = [], pointsRate, posSyncEnabled, addonGroups = [], onAddonGroupsChanged, hasBottleLocker, onClose, onSaved }) {
  const { mode, item } = state;
  const { user } = useAuth();
  // Tax groups to choose from (Prompt Z, Commit 4) — the venue's tax_table, or a food fallback for a
  // venue the migration hasn't reached. An item taxed by a group not in this list is surfaced loudly.
  const taxGroups = (Array.isArray(user?.venue?.tax_table) && user.venue.tax_table.length)
    ? user.venue.tax_table
    : [{ group: "food", label: "Food & non-alcoholic", rate: user?.venue?.gst_rate ?? 0 }];
  // Channels for the "Order types" control (AA-4): Dine-in / Takeaway / Room, each a
  // top-level service point carrying an order_type. Putting a channel's id in unavailable_at turns
  // the item off for that order type. The field also holds area/table ids (location axis) which this
  // control leaves untouched.
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    api.get("/service-points")
      .then(({ data }) => setChannels((data.service_points || []).filter(sp => sp.level === "channel" && sp.active !== false)))
      .catch(() => {});
  }, []);
  // POS-owned = a POS item id AND the venue is actively syncing. The importer owns
  // name/description/price/variants/type/category/availability + item-level add-on
  // links; those fields render but stay locked and the save omits them (the backend
  // also strips them — belt and braces). Disconnect the POS and the lock lifts.
  const isPosOwned = Boolean(item?.pos_item_id) && Boolean(posSyncEnabled);
  const posProvider = item?.pos_provider
    ? item.pos_provider.charAt(0).toUpperCase() + item.pos_provider.slice(1)
    : "your POS";
  const [form, setForm] = useState(
    item ? {
      name: item.name, description: item.description || "", price: item.price ?? "",
      category_id: item.category_id, item_type: item.item_type,
      item_code: item.item_code || "",
      tax_group: item.tax_group || "food",
      unavailable_at: Array.isArray(item.unavailable_at) ? item.unavailable_at : [],
      is_available: item.is_available, is_featured: item.is_featured,
      points_override: item.points_override ?? "",
      promo_line: item.promo_line || "",
      show_bottle_promo: item.show_bottle_promo || false,
      daily_quantity: item.daily_quantity ?? "",
      has_variants: item.has_variants || false,
      variants: item.has_variants && item.variants?.length
        ? item.variants.map(v => ({
            label: v.label || "",
            price: v.price || ""
          }))
        : DEFAULT_VARIANTS.map(v => ({ ...v })),
      tags: item.tags || [],
      is_must_try: item.is_must_try || false,
      mark_as_new: Boolean(item.marked_new_at),
      // Item-level add-on links [{group_id, min, max}] — normalised so the editor
      // always has numbers to work with.
      addon_groups: (item.addon_groups || []).map(l => ({
        group_id: l.group_id, min: Number(l.min ?? 0), max: Number(l.max ?? 0),
      })),
    } : {
      name: "", description: "", price: 100,
      category_id: state.categoryId, item_type: "veg",
      item_code: "",
      tax_group: "food",
      unavailable_at: [],
      is_available: true, is_featured: false, points_override: "",
      promo_line: "",
      show_bottle_promo: false,
      daily_quantity: "",
      has_variants: false,
      variants: DEFAULT_VARIANTS.map(v => ({ ...v })),
      tags: [],
      is_must_try: false,
      mark_as_new: false,
      addon_groups: [],
    },
  );
  const [loading, setLoading] = useState(false);
  const [tagCatalog, setTagCatalog] = useState([]);

  useEffect(() => {
    api.get("/menu/tag-catalog").then(({ data }) => setTagCatalog(data.tags || [])).catch(() => {});
  }, []);

  // Image upload state
  const resolveImgUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/static")) {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      return base ? `${base}${url}` : url;
    }
    return url;
  };
  const [imagePreview, setImagePreview] = useState(resolveImgUrl(item?.image_url));
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savedItemId, setSavedItemId] = useState(item?.id || null);
  const fileRef = React.useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // item_code is TabOn-owned on every venue (even POS-synced), so it is sent on all branches.
      const cleanCode = (form.item_code || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
      let payload;
      if (isPosOwned) {
        // POS owns name/description/price/variants/type/category/availability and the
        // item-level add-on links. Send ONLY the TabOn-owned merchandising fields —
        // never rebuild variants here (that would strip the POS variation ids), and
        // never send addon_groups (the importer manages them).
        payload = {
          is_featured: form.is_featured,
          points_override: form.points_override === "" ? null : Number(form.points_override),
          daily_quantity: form.daily_quantity === "" ? null : Number(form.daily_quantity),
          promo_line: form.promo_line?.trim() || null,
          show_bottle_promo: form.show_bottle_promo,
          tags: form.tags, is_must_try: form.is_must_try, mark_as_new: form.mark_as_new,
          item_code: cleanCode,
          tax_group: form.tax_group,   // TabOn-owned (the POS doesn't carry Indian tax groups), editable like item_code
          unavailable_at: form.unavailable_at,   // TabOn-owned location/order-type config (AA-4)
        };
      } else if (form.has_variants) {
        // Build cleaned variant list from rows that have a price entered
        const filledVariants = form.variants
          .filter(v => v.price !== "" && v.price !== null && v.price !== undefined && Number(v.price) > 0)
          .map(v => ({ label: v.label.trim() || "Variant", price: Number(v.price) }));
        if (filledVariants.length === 0) {
          toast.error("Add at least one variant price");
          setLoading(false);
          return;
        }
        payload = {
          name: form.name, description: form.description, category_id: form.category_id,
          item_type: form.item_type, is_available: form.is_available,
          is_featured: form.is_featured,
          points_override: null, promo_line: form.promo_line?.trim() || null,
          show_bottle_promo: form.show_bottle_promo,
          price: null, has_variants: true, variants: filledVariants,
          tags: form.tags, is_must_try: form.is_must_try, mark_as_new: form.mark_as_new,
          addon_groups: form.addon_groups,
          item_code: cleanCode,
          tax_group: form.tax_group,
          unavailable_at: form.unavailable_at,
        };
      } else {
        payload = {
          ...form,
          price: Number(form.price),
          points_override: form.points_override === "" ? null : Number(form.points_override),
          daily_quantity: form.daily_quantity === "" ? null : Number(form.daily_quantity),
          promo_line: form.promo_line?.trim() || null,
          show_bottle_promo: form.show_bottle_promo,
          has_variants: false, variants: null,
          tags: form.tags, is_must_try: form.is_must_try, mark_as_new: form.mark_as_new,
          addon_groups: form.addon_groups,
          item_code: cleanCode,
        };
      }
      let savedItem;
      if (mode === "edit") {
        const { data } = await api.put(`/menu/items/${item.id}`, payload);
        savedItem = data;
      } else {
        const { data } = await api.post("/menu/items", payload);
        savedItem = data;
      }
      setSavedItemId(savedItem?.id || item?.id);
      // Upload image if selected
      if (imageFile && savedItem?.id) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", imageFile);
          const { data: imgData } = await api.post(
            `/venue/menu/item/${savedItem.id}/image`, fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          setImagePreview(resolveImgUrl(imgData.image_url));
          toast.success(`Image saved (${imgData.size_kb} KB)`);
        } catch (imgErr) {
          toast.error("Image upload failed: " + formatError(imgErr));
        } finally {
          setUploading(false);
        }
      }
      toast.success(mode === "edit" ? "Item updated" : "Item added");
      onSaved(savedItem?.id || item?.id);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = async () => {
    if (savedItemId && item?.image_url) {
      try {
        await api.delete(`/venue/menu/item/${savedItemId}/image`);
        toast.success("Image removed");
      } catch (err) { toast.error(formatError(err)); }
    }
    setImagePreview(null);
    setImageFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const calcPts = form.points_override !== "" && form.points_override !== null
    ? Number(form.points_override)
    : Math.floor((Number(form.price) || 0) * pointsRate / 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit}
        className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{mode === "edit" ? "Edit Item" : "Add Item"}</h3>
          <button type="button" onClick={onClose} className="p-2 border border-white/10 rounded-lg"><X size={14} /></button>
        </div>
        {isPosOwned && (
          <div className="mb-4 flex items-start gap-2.5 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5">
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-[#A0A0A0] border border-white/10 shrink-0 mt-0.5">{posProvider}</span>
            <p className="text-[11px] text-[#A0A0A0] leading-relaxed">
              Name, price, variants and availability are managed in {posProvider}. Changes there appear here automatically. You can still edit points, tags, promos and other highlights below.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" span={2}>
            <input required data-testid="item-name" value={form.name} disabled={isPosOwned}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`} />
          </Field>
          <Field label="Description (max 300)" span={2}>
            <textarea data-testid="item-desc" maxLength={300} rows={2} value={form.description} disabled={isPosOwned}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`} />
          </Field>
          <Field label="Promo Line (optional, max 100)" span={2}>
            <input
              data-testid="item-promo-line"
              maxLength={100}
              placeholder="e.g. Can't finish tonight? Store your bottle 🍾"
              value={form.promo_line || ""}
              onChange={(e) => setForm({ ...form, promo_line: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
          </Field>
          <Field label="Item code (optional)" span={2}>
            <input
              data-testid="item-code"
              maxLength={10}
              placeholder="e.g. 403"
              value={form.item_code || ""}
              onChange={(e) => setForm({ ...form, item_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })}
              className="w-40 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono" />
            {(() => {
              const code = (form.item_code || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
              const dup = code && (allItems || []).some(x => x.id !== item?.id && (x.item_code || "") === code);
              return dup
                ? <p className="text-[11px] text-red-400 mt-1">Already used by another item — pick a different code.</p>
                : <p className="text-[11px] text-[#A0A0A0] mt-1">A short call-out code staff can search by and that prints on the KOT.</p>;
            })()}
          </Field>
          {/* Tax group selector is hidden while only one group can exist (AA-2 constrains the venue to
              food until the invoice reports tax per rate). Shown when there are 2+ groups OR when this
              item points at a group that no longer exists, so a pre-existing orphan can always be
              reassigned. The field, the PUT /menu/item-tax-groups endpoint and the "⚠ removed" handling
              all stay — they are correct and wanted again once multi-rate invoicing lands. */}
          {(taxGroups.length > 1 || (form.tax_group && !taxGroups.some((g) => g.group === form.tax_group))) && (
          <Field label="Tax group" span={2}>
            <select
              value={form.tax_group || "food"}
              onChange={(e) => setForm({ ...form, tax_group: e.target.value })}
              className="w-56 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none">
              {taxGroups.map((g) => (
                <option key={g.group} value={g.group}>{(g.label || g.group)} · {Number(g.rate) || 0}%</option>
              ))}
              {form.tax_group && !taxGroups.some((g) => g.group === form.tax_group) && (
                <option value={form.tax_group}>⚠ {form.tax_group} — removed, reassign</option>
              )}
            </select>
            {form.tax_group && !taxGroups.some((g) => g.group === form.tax_group)
              ? <p className="text-[11px] text-red-400 mt-1">This tax group no longer exists — pick a current one, or it bills at an undefined rate.</p>
              : <p className="text-[11px] text-[#A0A0A0] mt-1">Which GST rate applies to this item.</p>}
          </Field>
          )}
          {/* Available for (AA-4) — the order-type axis. Each channel is a toggle; turning one off
              puts its service-point id in unavailable_at, so the order path rejects the item for that
              order type. Area/table ids already in unavailable_at (the location axis) are preserved. */}
          {channels.length > 0 && (
          <Field label="Order types" span={2}>
            <div className="flex gap-2 flex-wrap">
              {channels.map((ch) => {
                const off = (form.unavailable_at || []).includes(ch.id);
                return (
                  <button key={ch.id} type="button"
                    aria-pressed={!off}
                    onClick={() => setForm(f => ({ ...f, unavailable_at: off
                      ? (f.unavailable_at || []).filter(x => x !== ch.id)
                      : [...(f.unavailable_at || []), ch.id] }))}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${off ? "border-white/10 text-[#777] line-through hover:border-white/30" : "bg-[#9D00FF]/20 border-[#9D00FF] text-white"}`}>
                    {ch.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#A0A0A0] mt-1">Tap an order type to turn this item off for it — e.g. a dine-in-only dish. (A specific table or area isn't editable here yet; use the bulk Order types editor.)</p>
          </Field>
          )}
          {tagCatalog.length > 0 && (
            <Field label="Tags" span={2}>
              <div className="flex gap-1.5 flex-wrap">
                {tagCatalog.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setForm(f => {
                      const nowSelected = !f.tags.includes(t.key);
                      const nextTags = nowSelected ? [...f.tags, t.key] : f.tags.filter(x => x !== t.key);
                      const nextType = (t.key === "egg" && nowSelected) ? "nonveg" : f.item_type;
                      return { ...f, tags: nextTags, item_type: nextType };
                    })}
                    className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                      form.tags.includes(t.key)
                        ? "bg-[#9D00FF]/15 border-[#9D00FF] text-[#D78CFF]"
                        : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                    }`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </Field>
          )}
          {hasBottleLocker && (
            <div className="col-span-2">
              <label className="flex items-center justify-between gap-3 cursor-pointer bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5">
                <div>
                  <div className="text-sm font-semibold text-white">Show bottle promo on QR menu</div>
                  <div className="text-[11px] text-[#A0A0A0] mt-0.5">Displays savings or locker message on this item (based on Settings)</div>
                </div>
                <input
                  type="checkbox"
                  checked={form.show_bottle_promo}
                  onChange={e => setForm({ ...form, show_bottle_promo: e.target.checked })}
                  className="accent-[#9D00FF] w-4 h-4 shrink-0"
                />
              </label>
            </div>
          )}
          {/* ── Multiple-price toggle ── */}
          <div className="col-span-2">
            <label className={`flex items-center justify-between gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 ${isPosOwned ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
              <div>
                <div className="text-sm font-semibold text-white">Multiple prices</div>
                <div className="text-[11px] text-[#A0A0A0] mt-0.5">For spirits, beer, wine — show 30 ML / 180 ML / Bottle prices</div>
              </div>
              <input
                type="checkbox"
                data-testid="item-has-variants"
                checked={form.has_variants}
                disabled={isPosOwned}
                onChange={e => setForm({ ...form, has_variants: e.target.checked })}
                className="accent-[#9D00FF] w-4 h-4 shrink-0"
              />
            </label>
          </div>

          {/* ── Single price (default) ── */}
          {!form.has_variants && (
            <>
              <Field label="Price (₹)">
                <input required type="number" min={0} data-testid="item-price" value={form.price} disabled={isPosOwned}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`} />
              </Field>
              <Field label="Points (read-only)">
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#D78CFF]">🏆 {calcPts}</div>
              </Field>
            </>
          )}

          {/* ── Variant rows ── */}
          {form.has_variants && (
            <div className="col-span-2 flex flex-col gap-2" data-testid="item-variants-section">
              <div className="text-[9px] uppercase tracking-wider text-[#A0A0A0]">Variant Prices</div>
              {form.variants.map((v, idx) => {
                const variantPts = v.price !== "" && Number(v.price) > 0
                  ? Math.floor(Number(v.price) * pointsRate / 100)
                  : null;
                return (
                  <div key={idx} className="flex items-center gap-2" data-testid={`item-variant-row-${idx}`}>
                    <input
                      type="text"
                      maxLength={20}
                      value={v.label}
                      disabled={isPosOwned}
                      onChange={e => {
                        const next = [...form.variants];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setForm({ ...form, variants: next });
                      }}
                      placeholder={`Option ${idx + 1}`}
                      data-testid={`item-variant-label-${idx}`}
                      className={`flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`}
                    />
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        <span className="text-[#A0A0A0] text-xs shrink-0">₹</span>
                        <input
                          type="number"
                          min={0}
                          value={v.price}
                          disabled={isPosOwned}
                          onChange={e => {
                            const next = [...form.variants];
                            next[idx] = { ...next[idx], price: e.target.value };
                            setForm({ ...form, variants: next });
                          }}
                          placeholder="Price"
                          data-testid={`item-variant-price-${idx}`}
                          className={`w-24 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>
                      {variantPts !== null && pointsRate > 0 && (
                        <div className="text-[10px] text-[#A0A0A0] pl-4">≈ {variantPts} pts</div>
                      )}
                    </div>
                    {!isPosOwned && form.variants.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = form.variants.filter((_, i) => i !== idx);
                          setForm({ ...form, variants: next });
                        }}
                        className="text-[#A0A0A0] hover:text-white text-lg leading-none shrink-0"
                        data-testid={`item-variant-remove-${idx}`}
                        aria-label="Remove variant"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
              {!isPosOwned && form.variants.length < 6 && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, variants: [...form.variants, { label: "", price: "" }] })}
                  className="self-start text-xs text-[#D78CFF] hover:text-white mt-0.5"
                  data-testid="item-variant-add"
                >
                  + Add variant
                </button>
              )}
              {!isPosOwned && (
                <p className="text-[10px] text-[#A0A0A0] mt-0.5">Empty rows are not saved. At least one price is required.</p>
              )}
            </div>
          )}

          <Field label="Item Type">
            <select data-testid="item-type" value={form.item_type} disabled={isPosOwned}
              onChange={(e) => setForm({ ...form, item_type: e.target.value })}
              className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`}>
              {ITEM_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select data-testid="item-cat" value={form.category_id} disabled={isPosOwned}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white ${isPosOwned ? "opacity-60 cursor-not-allowed" : ""}`}>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          {!form.has_variants && (
            <Field label="Points override (optional)">
              <input type="number" min={0} data-testid="item-pts-override" value={form.points_override}
                onChange={(e) => setForm({ ...form, points_override: e.target.value })}
                placeholder="Use calculated"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </Field>
          )}
          {!form.has_variants && (
            <div className="col-span-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1">
                Daily quantity limit <span className="font-normal normal-case text-[#555]">(optional — for ordering)</span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={9999}
                  value={form.daily_quantity}
                  onChange={e => setForm({ ...form, daily_quantity: e.target.value })}
                  placeholder="Unlimited"
                  className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                />
                {form.daily_quantity !== "" && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, daily_quantity: "" })}
                    className="text-xs text-[#A0A0A0] hover:text-white"
                  >
                    Clear (unlimited)
                  </button>
                )}
              </div>
              <p className="text-[11px] text-[#A0A0A0] mt-1">
                Item auto-marks as sold out when this many are ordered. Resets at midnight.
              </p>
              {item?.daily_sold > 0 && (
                <p className="text-[11px] text-emerald-400 mt-0.5">
                  {item.daily_sold} sold today
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 col-span-2 mt-1">
            <label className={`flex items-center gap-2 text-sm ${isPosOwned ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
              title={isPosOwned ? `Availability is managed in ${posProvider} — mark it there and it updates here within seconds` : undefined}>
              <input type="checkbox" checked={form.is_available} disabled={isPosOwned}
                onChange={(e) => setForm({ ...form, is_available: e.target.checked })}
                className="accent-[#9D00FF]" /> Available
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                className="accent-[#9D00FF]" /> ⭐ Bestseller
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_must_try}
                onChange={(e) => setForm({ ...form, is_must_try: e.target.checked })}
                className="accent-[#9D00FF]" /> ✨ Must Try
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.mark_as_new}
                onChange={(e) => setForm({ ...form, mark_as_new: e.target.checked })}
                className="accent-[#9D00FF]" /> 🆕 Mark as New
            </label>
          </div>

          {/* Add-ons (item-level links to add-on groups) */}
          <ItemAddonEditor
            links={form.addon_groups}
            setLinks={(next) => setForm(f => ({ ...f, addon_groups: next }))}
            groups={addonGroups}
            disabled={isPosOwned}
            posProvider={posProvider}
            onReloadGroups={onAddonGroupsChanged}
          />

          {/* Image Upload */}
          <div className="col-span-2 border-t border-white/10 pt-3 mt-1">
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2 flex items-center gap-1.5">
              <Camera size={11} /> Item Image (Optional)
            </div>
            {imagePreview ? (
              <div className="flex items-start gap-3">
                <img
                  src={imagePreview}
                  alt="Item preview"
                  data-testid="item-image-preview"
                  className="w-24 h-18 object-cover rounded-lg border border-white/10"
                  style={{ aspectRatio: "4/3", height: 72 }}
                />
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                    <Check size={11} /> Image ready
                  </p>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="text-xs text-[#9D00FF] hover:underline"
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="text-xs text-red-400 hover:underline"
                    data-testid="remove-item-image"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-item-image"
                className="w-full border-2 border-dashed border-white/15 hover:border-[#9D00FF]/50 rounded-lg p-4 text-center transition group"
              >
                <Camera size={20} className="mx-auto text-[#A0A0A0] group-hover:text-[#9D00FF] mb-1.5 transition" />
                <p className="text-xs text-[#A0A0A0] group-hover:text-white transition">
                  Upload Image
                </p>
                <p className="text-[10px] text-[#6B6B6B] mt-0.5">
                  JPG, PNG or WebP · Max 10 MB · Auto-cropped 4:3
                </p>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handleFileChange}
              data-testid="item-image-input"
            />
            {uploading && (
              <p className="text-xs text-[#9D00FF] mt-1.5 flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Uploading image…
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="border border-white/10 rounded-lg px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={loading || uploading} data-testid="item-save"
            className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-5 py-2">
            {loading ? "Saving…" : (mode === "edit" ? "Save" : "Add Item")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <label className={`block col-span-${span === 2 ? "2" : "1"}`} style={span === 2 ? { gridColumn: "span 2" } : {}}>
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------- Add-on groups management (native editor) ----------
// The POS importer writes into the same collections; imported groups/items are shown
// read-only and labelled with their provider (edits happen in the POS).

function AddonOptionRow({ option, groupIsPos, onReload }) {
  const [name, setName] = useState(option.name || "");
  const [price, setPrice] = useState(option.price ?? 0);
  const [type, setType] = useState(option.item_type || "veg");
  const [busy, setBusy] = useState(false);
  const dirty = name !== (option.name || "")
    || String(price) !== String(option.price ?? 0)
    || type !== (option.item_type || "veg");

  const save = async () => {
    if (!name.trim()) { toast.error("Option needs a name"); return; }
    setBusy(true);
    try {
      await api.put(`/menu/addon-items/${option.id}`, {
        name: name.trim(),
        price: price === "" ? 0 : Number(price) || 0,
        item_type: type,
      });
      await onReload();
      toast.success("Option saved");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete option “${option.name}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/menu/addon-items/${option.id}`);
      await onReload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  if (groupIsPos) {
    return (
      <div className="flex items-center gap-2 text-sm px-2.5 py-1.5">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: (option.item_type === "nonveg") ? "#ef4444" : "#22c55e" }} />
        <span className="flex-1 truncate text-white">{option.name}</span>
        <span className="shrink-0 text-[#A0A0A0]">₹{Number(option.price || 0)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: (type === "nonveg") ? "#ef4444" : "#22c55e" }} />
      <input value={name} onChange={e => setName(e.target.value)}
        className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white" />
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-[#A0A0A0] text-xs">₹</span>
        <input type="number" min={0} value={price} onChange={e => setPrice(e.target.value)}
          className="w-16 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
      </div>
      <select value={type} onChange={e => setType(e.target.value)}
        className="bg-[#1A1A1A] border border-white/10 rounded-lg px-1.5 py-1.5 text-xs text-white shrink-0">
        <option value="veg">Veg</option>
        <option value="nonveg">Non-veg</option>
      </select>
      {dirty && (
        <button type="button" onClick={save} disabled={busy}
          className="text-emerald-400 hover:text-emerald-300 shrink-0" title="Save option"><Check size={14} /></button>
      )}
      <button type="button" onClick={remove} disabled={busy}
        className="text-[#EF4444] hover:text-red-400 shrink-0" title="Delete option"><Trash2 size={13} /></button>
    </div>
  );
}

function AddonGroupRow({ group, onReload }) {
  const isPos = Boolean(group.pos_group_id);
  const provider = group.pos_provider
    ? group.pos_provider.charAt(0).toUpperCase() + group.pos_provider.slice(1)
    : "POS";
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name || "");
  const [optName, setOptName] = useState("");
  const [optPrice, setOptPrice] = useState("");
  const [optType, setOptType] = useState("veg");
  const [busy, setBusy] = useState(false);
  const used = group.used_by_count || 0;
  const options = group.items || [];

  const saveName = async () => {
    const nm = name.trim();
    if (!nm) { toast.error("Group needs a name"); return; }
    setBusy(true);
    try {
      await api.put(`/menu/addon-groups/${group.id}`, { name: nm });
      await onReload();
      setEditing(false);
      toast.success("Group renamed");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.put(`/menu/addon-groups/${group.id}`, { is_active: !group.is_active });
      await onReload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    const warn = used > 0
      ? `Delete “${group.name}”? It's used on ${used} item${used === 1 ? "" : "s"} — it will be removed from ${used === 1 ? "it" : "them"} and its options deleted.`
      : `Delete “${group.name}” and its options?`;
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      await api.delete(`/menu/addon-groups/${group.id}`);
      await onReload();
      toast.success("Add-on group deleted");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const addOption = async () => {
    if (!optName.trim()) { toast.error("Option needs a name"); return; }
    setBusy(true);
    try {
      await api.post(`/menu/addon-groups/${group.id}/items`, {
        name: optName.trim(),
        price: optPrice === "" ? 0 : Number(optPrice) || 0,
        item_type: optType,
      });
      await onReload();
      setOptName(""); setOptPrice(""); setOptType("veg");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="py-2.5">
      <div className="flex items-center gap-2.5">
        <button type="button" onClick={() => setExpanded(v => !v)} className="flex items-center gap-2 min-w-0 text-left flex-1">
          {expanded ? <ChevronDown size={15} className="text-[#A0A0A0] shrink-0" /> : <ChevronRight size={15} className="text-[#A0A0A0] shrink-0" />}
          {editing ? (
            <input autoFocus value={name} onClick={e => e.stopPropagation()} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") saveName(); }}
              className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white" />
          ) : (
            <span className="font-semibold text-sm truncate">{group.name}</span>
          )}
          <span className="text-[11px] text-[#A0A0A0] shrink-0">{options.length} option{options.length === 1 ? "" : "s"}</span>
          {used > 0 && <span className="text-[9px] font-bold uppercase tracking-wider bg-[#9D00FF]/15 text-[#9D00FF] rounded px-1.5 py-0.5 shrink-0">Used on {used}</span>}
          {isPos && <span className="text-[9px] font-bold uppercase tracking-wider bg-white/5 text-[#A0A0A0] border border-white/10 rounded px-1.5 py-0.5 shrink-0">{provider}</span>}
          {!group.is_active && <span className="text-[9px] font-bold uppercase tracking-wider bg-white/5 text-[#A0A0A0] border border-white/10 rounded px-1.5 py-0.5 shrink-0">Hidden</span>}
        </button>
        {!isPos && (
          <>
            <label className="flex items-center gap-1 text-[11px] text-[#A0A0A0] cursor-pointer shrink-0">
              <input type="checkbox" checked={group.is_active} onChange={toggleActive} className="accent-[#9D00FF]" /> Active
            </label>
            {editing ? (
              <button type="button" onClick={saveName} disabled={busy} className="text-emerald-400 hover:text-emerald-300 shrink-0" title="Save name"><Check size={15} /></button>
            ) : (
              <button type="button" onClick={() => { setName(group.name || ""); setEditing(true); }} className="text-[#A0A0A0] hover:text-white shrink-0" title="Rename"><Pencil size={14} /></button>
            )}
            <button type="button" onClick={remove} disabled={busy} className="text-[#EF4444] hover:text-red-400 shrink-0" title="Delete group"><Trash2 size={14} /></button>
          </>
        )}
      </div>

      {expanded && (
        <div className="mt-2.5 ml-6 flex flex-col gap-2">
          {options.length === 0 && <div className="text-[11px] text-[#A0A0A0]">No options yet.</div>}
          {options.map(o => <AddonOptionRow key={o.id} option={o} groupIsPos={isPos} onReload={onReload} />)}
          {isPos ? (
            <p className="text-[11px] text-[#A0A0A0] mt-0.5">Managed in {provider} — options update automatically.</p>
          ) : (
            <div className="flex items-center gap-1.5 mt-0.5 border-t border-white/5 pt-2">
              <input value={optName} onChange={e => setOptName(e.target.value)} placeholder="New option"
                onKeyDown={e => { if (e.key === "Enter") addOption(); }}
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white" />
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[#A0A0A0] text-xs">₹</span>
                <input type="number" min={0} value={optPrice} onChange={e => setOptPrice(e.target.value)} placeholder="0"
                  className="w-16 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
              </div>
              <select value={optType} onChange={e => setOptType(e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 rounded-lg px-1.5 py-1.5 text-xs text-white shrink-0">
                <option value="veg">Veg</option>
                <option value="nonveg">Non-veg</option>
              </select>
              <button type="button" onClick={addOption} disabled={busy}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shrink-0">Add</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddonGroupsSection({ groups, onReload }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const nm = newName.trim();
    if (!nm) return;
    setBusy(true);
    try {
      await api.post("/menu/addon-groups", { name: nm });
      await onReload();
      setNewName("");
      toast.success("Add-on group created");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const sorted = [...groups].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6">
      <h2 className="text-lg font-bold mb-1">Add-ons</h2>
      <p className="text-xs text-[#A0A0A0] mb-3">Reusable option groups guests pick from — sauces, sides, spice level. Attach them to items from the item editor.</p>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input value={newName} onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") create(); }}
          placeholder="New add-on group name"
          className="flex-1 min-w-[160px] bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <button onClick={create} disabled={busy}
          className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-4 text-sm font-semibold">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="flex flex-col divide-y divide-white/5">
        {sorted.map(g => <AddonGroupRow key={g.id} group={g} onReload={onReload} />)}
        {groups.length === 0 && <div className="py-4 text-sm text-[#A0A0A0]">No add-on groups yet. Create one above, then attach it to items.</div>}
      </div>
    </div>
  );
}

// ---------- Menu Appearance (branding) ----------

const COLOR_FIELDS = [
  { k: "menu_primary_color", label: "Primary Colour", help: "Buttons, points badges, active tabs" },
  { k: "menu_secondary_color", label: "Secondary Colour", help: "Header background, card headers" },
  { k: "menu_background_color", label: "Page Background", help: "Overall page background" },
  { k: "menu_card_color", label: "Card Background", help: "Individual item card background" },
  { k: "menu_text_color", label: "Primary Text", help: "Item names, prices" },
  { k: "menu_accent_text_color", label: "Muted Text", help: "Descriptions, helper text" },
];

function AppearanceSection({ settings, onSaved }) {
  const defaults = settings.branding_defaults || {};
  const initial = React.useMemo(() => {
    // Start from effective branding (defaults merged with any custom values).
    const obj = {};
    COLOR_FIELDS.forEach(({ k }) => {
      obj[k] = (settings.branding?.[k] || defaults[k] || "").toUpperCase();
    });
    return obj;
  }, [settings]);

  const [colors, setColors] = useState(initial);
  const [logoUrl, setLogoUrl] = useState(settings.branding?.menu_logo_url || "");
  const [coverUrl, setCoverUrl] = useState(settings.menu_cover_url || "");
  const [promoCoverUrl, setPromoCoverUrl] = useState(settings.menu_promo_cover_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [promoUploading, setPromoUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const coverFileInputRef = React.useRef(null);
  const promoFileInputRef = React.useRef(null);

  React.useEffect(() => {
    setColors(initial);
    setLogoUrl(settings.branding?.menu_logo_url || "");
    setCoverUrl(settings.menu_cover_url || "");
    setPromoCoverUrl(settings.menu_promo_cover_url || "");
  }, [initial, settings]);

  const setColor = (k, v) => {
    let val = (v || "").trim();
    if (val && !val.startsWith("#")) val = "#" + val;
    setColors((c) => ({ ...c, [k]: val.toUpperCase() }));
  };

  const resetAll = () => {
    const obj = {};
    COLOR_FIELDS.forEach(({ k }) => { obj[k] = defaults[k]; });
    setColors(obj);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Logo must be PNG or JPG");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo exceeds 2 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/menu/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.menu_logo_url);
      toast.success("Logo uploaded");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the venue logo?")) return;
    try {
      await api.delete("/menu/logo");
      setLogoUrl("");
      toast.success("Logo removed");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Cover must be PNG, JPG, or WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Cover image exceeds 5 MB");
      return;
    }
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/menu/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverUrl(data.menu_cover_url);
      toast.success("Cover image uploaded");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setCoverUploading(false); }
  };

  const removeCover = async () => {
    if (!window.confirm("Remove the cover image?")) return;
    try {
      await api.delete("/menu/cover");
      setCoverUrl("");
      toast.success("Cover image removed");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadPromoCover = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Promo cover must be PNG, JPG, or WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Promo cover exceeds 5 MB");
      return;
    }
    setPromoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/menu/promo-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPromoCoverUrl(data.menu_promo_cover_url);
      toast.success("Promo cover uploaded");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setPromoUploading(false); }
  };

  const removePromoCover = async () => {
    if (!window.confirm("Remove the promo cover?")) return;
    try {
      await api.delete("/menu/promo-cover");
      setPromoCoverUrl("");
      toast.success("Promo cover removed");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  const save = async () => {
    // Validate hex values client-side before sending.
    for (const { k, label } of COLOR_FIELDS) {
      if (!/^#[0-9A-Fa-f]{6}$/.test(colors[k])) {
        toast.error(`${label} must be a valid 6-digit hex like #9D00FF`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/menu/settings", colors);
      toast.success("Menu appearance saved");
      await onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold">Menu Appearance</h2>
        <button
          type="button"
          onClick={resetAll}
          data-testid="appearance-reset"
          className="text-xs text-[#A0A0A0] hover:text-white underline"
        >
          Reset to TabOn defaults
        </button>
      </div>
      <p className="text-xs text-[#A0A0A0] mb-4">Apply your venue's visual identity to the public menu. These settings do not affect the TabOn dashboard.</p>

      <div className="grid md:grid-cols-3 gap-5">
        {/* Logo + Cover (stacked in left column) */}
        <div className="md:col-span-1 flex flex-col gap-4">
          {/* Logo */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Upload your venue logo</div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 flex flex-col items-center">
              {logoUrl ? (
                <img src={logoUrl} alt="Venue logo" data-testid="appearance-logo-preview"
                  style={{ maxHeight: 80 }} className="object-contain" />
              ) : (
                <div data-testid="appearance-logo-placeholder" className="text-sm text-[#A0A0A0] italic text-center">No logo — venue name text will show on public menu.</div>
              )}
              <div className="flex gap-2 mt-3">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  data-testid="appearance-upload-btn"
                  className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-2 text-xs font-semibold">
                  {uploading ? "Uploading…" : (logoUrl ? "Replace" : "Upload")}
                </button>
                {logoUrl && (
                  <button type="button" onClick={removeLogo} data-testid="appearance-logo-remove"
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold text-[#EF4444]">
                    Remove
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }}
              />
              <p className="text-[11px] text-[#A0A0A0] mt-2 text-center">PNG or JPG · Max 2 MB · Shown max 80px tall</p>
            </div>
          </div>

          {/* Cover image */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1">Menu Cover Photo (hero image)</div>
            <p className="text-[11px] text-[#A0A0A0] mb-1.5">Full-screen image shown at top of your QR menu page</p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 flex flex-col items-center">
              {coverUrl ? (
                <>
                  <img src={coverUrl} alt="Cover preview" data-testid="appearance-cover-preview"
                    className="w-full rounded-lg object-cover mb-3"
                    style={{ maxHeight: 80, objectFit: "cover" }} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => coverFileInputRef.current?.click()} disabled={coverUploading}
                      data-testid="appearance-cover-upload-btn"
                      className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-2 text-xs font-semibold">
                      {coverUploading ? "Uploading…" : "Replace"}
                    </button>
                    <button type="button" onClick={removeCover} data-testid="appearance-cover-remove"
                      className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold text-[#EF4444]">
                      Remove
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div data-testid="appearance-cover-placeholder" className="text-sm text-[#A0A0A0] italic text-center mb-3">
                    No cover — a gradient using your primary colour will show.
                  </div>
                  <button type="button" onClick={() => coverFileInputRef.current?.click()} disabled={coverUploading}
                    data-testid="appearance-cover-upload-btn"
                    className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-2 text-xs font-semibold">
                    {coverUploading ? "Uploading…" : "Upload Cover"}
                  </button>
                </>
              )}
              <input ref={coverFileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { uploadCover(e.target.files?.[0]); e.target.value = ""; }}
              />
              <p className="text-[11px] text-[#A0A0A0] mt-2 text-center">PNG, JPG or WebP · Max 5 MB</p>
            </div>
          </div>

          {/* Promo cover image */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1">Menu Cover Promo Photo</div>
            <p className="text-[11px] text-[#A0A0A0] mb-1.5">When set, replaces your standard cover and shows full-screen with no text overlay.</p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 flex flex-col items-center">
              {promoCoverUrl ? (
                <>
                  <img src={promoCoverUrl} alt="Promo cover preview"
                    className="w-full rounded-lg object-cover mb-3"
                    style={{ maxHeight: 80, objectFit: "cover" }} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => promoFileInputRef.current?.click()} disabled={promoUploading}
                      className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-2 text-xs font-semibold">
                      {promoUploading ? "Uploading…" : "Replace"}
                    </button>
                    <button type="button" onClick={removePromoCover}
                      className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold text-[#EF4444]">
                      Remove
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm text-[#A0A0A0] italic text-center mb-3">
                    No promo — your standard cover (or gradient) will show.
                  </div>
                  <button type="button" onClick={() => promoFileInputRef.current?.click()} disabled={promoUploading}
                    className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-3 py-2 text-xs font-semibold">
                    {promoUploading ? "Uploading…" : "Upload Promo"}
                  </button>
                </>
              )}
              <input ref={promoFileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { uploadPromoCover(e.target.files?.[0]); e.target.value = ""; }}
              />
              <p className="text-[11px] text-[#A0A0A0] mt-2 text-center">Recommended: 1080 × 1920 px (portrait). Max 5 MB.</p>
            </div>
          </div>
        </div>

        {/* Colour pickers */}
        <div className="md:col-span-2 grid sm:grid-cols-2 gap-3">
          {COLOR_FIELDS.map(({ k, label, help }) => (
            <div key={k}>
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">{label}</div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  data-testid={`appearance-picker-${k}`}
                  value={/^#[0-9A-Fa-f]{6}$/.test(colors[k]) ? colors[k] : "#000000"}
                  onChange={(e) => setColor(k, e.target.value)}
                  className="h-10 w-14 bg-transparent border border-white/10 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  data-testid={`appearance-hex-${k}`}
                  value={colors[k]}
                  onChange={(e) => setColor(k, e.target.value)}
                  maxLength={7}
                  className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono uppercase"
                />
              </div>
              <p className="text-[11px] text-[#A0A0A0] mt-1">{help}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">Preview — how your menu items will appear to customers</div>
        <LivePreview colors={colors} logoUrl={logoUrl} />
      </div>

      <div className="flex justify-end mt-5">
        <button type="button" onClick={save} disabled={saving}
          data-testid="appearance-save"
          className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 rounded-lg px-5 py-2.5 text-sm font-bold">
          {saving ? "Saving…" : "Save Appearance"}
        </button>
      </div>
    </div>
  );
}

function LivePreview({ colors, logoUrl }) {
  return (
    <div
      data-testid="appearance-preview"
      className="rounded-xl p-4 border border-white/10"
      style={{ background: colors.menu_background_color }}
    >
      <div className="text-center mb-3">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo preview" style={{ maxHeight: 40, margin: "0 auto 4px" }} />
        ) : null}
        <div style={{ color: colors.menu_text_color }} className="font-black text-base">Your Venue Name</div>
      </div>
      <div
        className="rounded-lg p-3 flex items-start gap-3 border border-white/10"
        style={{ background: colors.menu_card_color }}
      >
        <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 bg-emerald-500" />
        <div className="flex-1">
          <div className="font-bold text-sm" style={{ color: colors.menu_text_color }}>Kul'hadd Chai</div>
          <div className="text-xs mt-0.5" style={{ color: colors.menu_accent_text_color }}>
            Traditional clay-cup masala chai brewed slow with fresh herbs.
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="font-black" style={{ color: colors.menu_text_color }}>₹25</div>
          <div className="text-xs font-bold" style={{ color: colors.menu_primary_color }}>🏆 2 pts</div>
        </div>
      </div>
      <div
        className="mt-3 rounded-lg px-3 py-2 text-[11px] inline-block"
        style={{ background: colors.menu_secondary_color, color: colors.menu_text_color }}
      >
        📱 Check your points balance
      </div>
    </div>
  );
}

